import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  SlidersHorizontal,
  Download,
  Upload,
  FileSpreadsheet,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  MoreVertical,
  ChevronDown,
  Check,
  Loader2,
  RotateCcw,
  Maximize2,
  Minimize2,
  Filter,
  FilterX
} from "lucide-react";

import { useUIStore } from "../store/uiStore";
import { toast } from "react-toastify";
import type { QueryParams, Filter as FilterType, SimpleFilter } from "../types";
import Logo from "../assets/logo.png";
import { tenantConfig } from "../config/tenantConfig";
import { Spinner } from "./Spinner";



const DraggableTableHeader = ({ header, alignClasses, columns, tableKey }: any) => {
  const isStatic = header.column.id === 'select' || header.column.id === 'index';
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: header.column.id,
    disabled: isStatic,
  });

  const style: React.CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: 'relative',
    transform: CSS.Translate.toString(transform),
    transition: 'width transform 0.2s ease-in-out',
    width: header.getSize(),
    minWidth: header.getSize(),
    maxWidth: header.getSize(),
    zIndex: isDragging ? 1 : 0,
  };

  const originalCol = columns.find((c: any) => String(c.field) === header.column.id);
  const alignMap = originalCol?.align || "left";
  const headerActions = originalCol?.headerActions;
  const isSortable = header.column.getCanSort();
  const isSquished = header.getSize() < 100;
  const paddingClasses = isStatic
    ? 'px-0'
    : isSquished
      ? 'py-2 px-1'
      : 'py-3 pl-1.5 sm:pl-2 pr-2 sm:pr-3';

  return (
    <th
      ref={setNodeRef}
      style={style}
      className={`text-[11px] font-black uppercase tracking-widest text-text/50 bg-card border-x border-borderColor shadow-[inset_0_-1px_0_0_var(--border),inset_0_1px_0_0_var(--border)] ${alignClasses[alignMap]} relative group/th select-none ${isDragging ? 'bg-bgAlt shadow-lg border-primary/20 z-10' : ''} ${paddingClasses}`}
    >
      {header.isPlaceholder ? null : (
        <div className={`flex items-center gap-2 w-full h-full ${alignMap === "center" ? "justify-center" : alignMap === "right" ? "justify-end" : ""}`}>
          <div
            {...(!isStatic ? attributes : {})}
            {...(!isStatic ? listeners : {})}
            onClick={isSortable ? (e) => { e.stopPropagation(); header.column.getToggleSortingHandler()?.(e); } : undefined}
            className={`flex items-center gap-2 select-none min-w-0 flex-1 ${!isStatic ? 'cursor-grab active:cursor-grabbing' : ''} ${isSortable ? "hover:text-text transition-colors" : ""}`}
          >
            {isStatic ? (
              <div className="flex-1 flex justify-center">
                {flexRender(header.column.columnDef.header, header.getContext())}
              </div>
            ) : (
              <div className="line-clamp-2 break-normal whitespace-normal text-ellipsis overflow-hidden flex-1" title={typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : undefined}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </div>
            )}
            {isSortable && (
              <span className={`text-text/30 group-hover/th:text-primary transition-colors shrink-0 ${isSquished && !header.column.getIsSorted() ? 'hidden group-hover/th:inline-block' : 'inline-block'}`}>
                {{
                  asc: <ArrowUp size={14} className="text-primary" />,
                  desc: <ArrowDown size={14} className="text-primary" />,
                }[header.column.getIsSorted() as string] ?? <ArrowUpDown size={13} />}
              </span>
            )}
          </div>
          {headerActions && (
            <div className="shrink-0 flex items-center z-[2]" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
              {headerActions}
            </div>
          )}
        </div>
      )}

      {header.column.getCanResize() && (
        <div
          onMouseDown={(e) => { e.stopPropagation(); header.getResizeHandler()(e); }}
          onTouchStart={(e) => { e.stopPropagation(); header.getResizeHandler()(e); }}
          className={`absolute right-[-4px] top-1/4 h-1/2 w-[8px] cursor-col-resize select-none touch-none hover:bg-primary/20 transition-colors z-20 flex justify-center`}
        >
          <div className={`h-full w-[2px] transition-colors ${header.column.getIsResizing() ? 'bg-primary' : 'bg-text/20'}`}></div>
        </div>
      )}
    </th>
  );
};

export interface LocalDataTableColumn<T> {
  field: keyof T | string;
  header?: string | React.ReactNode;
  headerActions?: React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  align?: "left" | "right" | "center";
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  textOverflow?: "wrap" | "ellipsis" | "scroll";
  maxLines?: number;
  body?: (data: T, options?: any) => React.ReactNode;
  cellHoverActions?: (data: T, options?: any) => React.ReactNode;
}

interface ExcelModuleProps {
  onExport: (type: "template" | "all") => Promise<void>;
  onImport: (file: File) => Promise<{ success: boolean; summary: any }>;
}

interface EDataTableProps<T extends object> {
  useStore: () => any;
  title?: string;
  subTitle?: React.ReactNode;
  actions?: React.ReactNode;
  filterBar?: React.ReactNode;
  placeholder: string;
  searchFields: { field: string; operator: SimpleFilter["operator"] }[];
  columns: LocalDataTableColumn<T>[];
  dataKey: keyof T;
  tableKey: string;
  excelModule?: ExcelModuleProps;
  selectable?: boolean;
  onSelectionChange?: (e: any) => void;
  onRowClick?: (data: T, event: React.MouseEvent) => void;
  filters?: FilterType[];
  scrollh?: number;
  showSelection?: boolean;
  selection?: any[];
  showIndex?: boolean;
  defaultFilterExpanded?: boolean;
  virtualize?: boolean;
}

export const EDataTable = <T extends object>({
  useStore,
  title,
  subTitle,
  filterBar,
  placeholder,
  searchFields,
  actions,
  columns,
  dataKey,
  tableKey,
  excelModule,
  selectable,
  onSelectionChange,
  filters: propsFilters,
  defaultFilterExpanded = false,
  virtualize = false,
  ...props
}: EDataTableProps<T>) => {
  const store = useStore();
  const { data, total, loading, queryParams, fetchDataDynamic, setQueryParams } = store;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [importing, setImporting] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const initialSortMount = useRef(true);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedSearchField, setSelectedSearchField] = useState<string>("all");

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isFilterExpanded, setIsFilterExpanded] = useState(defaultFilterExpanded);

  useEffect(() => {
    if (onSelectionChange && data) {
      const selectedItems = Object.keys(rowSelection).map(index => {
        return data[Number(index)] as any;
      }).filter(Boolean);
      onSelectionChange({ value: selectedItems });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  useEffect(() => {
    if (props.selection !== undefined && data) {
      const newSelection: Record<string, boolean> = {};
      props.selection.forEach(item => {
        const index = data.findIndex((d: any) => d[dataKey as keyof typeof d] === item[dataKey as keyof typeof item]);
        if (index !== -1) {
          newSelection[index] = true;
        }
      });
      // Sadece gerçekten değişmişse güncelle (Sonsuz döngüyü önlemek için)
      if (JSON.stringify(newSelection) !== JSON.stringify(rowSelection)) {
        setRowSelection(newSelection);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selection, data, dataKey]);

  const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];
  const currentPageIndex = queryParams?.pageIndex || 1;
  const currentPageSize = queryParams?.pageSize || 10;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setShowMobileActions(false);
      }
    };
    if (showMobileActions) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMobileActions]);

  const FIELD_LABEL_MAP: Record<string, string> = {
    "order.orderNumber": "Sipariş No",
    "order.invoiceNumber": "Fatura No",
    "order.account.name": "Müşteri Adı",
    "order.account.phone": "Telefon",
    "order.shippingAddress.addressLine": "Sevk Adresi",
    "name": "Ürün Adı",
    "barcode": "Barkod",
    "sku": "Stok Kodu",
    "code": "Kod",
    "description": "Açıklama",
    "phone": "Telefon",
    "email": "E-Posta",
    "taxNumber": "Vergi No",
  };

  const searchFieldOptions = useMemo(() => {
    return (searchFields || []).map((sf) => {
      // 1. Direct column match
      let col = columns.find((c) => String(c.field) === sf.field);
      // 2. Substring column match (e.g. order.account.name -> account)
      if (!col) {
        col = columns.find((c) => sf.field.includes(String(c.field)) || String(c.field).includes(sf.field));
      }

      let label = FIELD_LABEL_MAP[sf.field];
      if (!label && col && typeof col.header === "string" && col.header.trim().length > 0) {
        label = col.header;
      }
      if (!label) {
        const parts = sf.field.split(".");
        const lastPart = parts[parts.length - 1];
        label = lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
      }

      return {
        value: sf.field,
        label,
      };
    });
  }, [searchFields, columns]);

  const currentSearchFieldLabel = useMemo(() => {
    if (selectedSearchField === "all") return "Tümü";
    const found = searchFieldOptions.find((o) => o.value === selectedSearchField);
    return found ? found.label : selectedSearchField;
  }, [selectedSearchField, searchFieldOptions]);

  const { getTableColumns, setTableColumns } = useUIStore();
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const saved = getTableColumns(tableKey);
    if (saved && saved.columns && saved.columns.length > 0) {
      const visibilityMap: Record<string, boolean> = {};
      columns.forEach((col) => {
        visibilityMap[String(col.field)] = saved.columns.includes(String(col.field));
      });
      return visibilityMap;
    }
    const defaultMap: Record<string, boolean> = {};
    columns.forEach((col) => {
      defaultMap[String(col.field)] = true;
    });
    return defaultMap;
  });

  useEffect(() => {
    const visibleFields = Object.keys(columnVisibility).filter((key) => columnVisibility[key]);
    const existingConfig = getTableColumns(tableKey);
    const existingColumns = existingConfig?.columns || [];

    // Yalnızca sütun görünürlüğü değiştiyse kaydet
    const isDifferent = visibleFields.length !== existingColumns.length || !visibleFields.every(v => existingColumns.includes(v));

    if (isDifferent) {
      setTableColumns(tableKey, {
        ...(existingConfig || {}),
        columns: visibleFields,
        widths: existingConfig?.widths || {}
      });
    }
  }, [columnVisibility, tableKey, setTableColumns, getTableColumns]);

  useEffect(() => {
    if (loading) {
      if (searchValue.trim()) {
        useUIStore.getState().setSyncStatus('syncing', 'Arama yapılıyor...');
      } else {
        useUIStore.getState().setSyncStatus('syncing', `${title ? title + ' çekiliyor...' : 'Liste yükleniyor...'}`);
      }
    } else {
      if (useUIStore.getState().syncStatus === 'syncing') {
        useUIStore.getState().setSyncStatus('idle');
      }
    }
  }, [loading, searchValue, title]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const textFilters: SimpleFilter[] = [];
      if (searchValue.trim()) {
        if (selectedSearchField === "all") {
          searchFields.forEach((item) => {
            textFilters.push({
              field: item.field,
              operator: item.operator,
              value: searchValue.trim(),
            });
          });
        } else {
          const targetField = searchFields.find(f => f.field === selectedSearchField);
          textFilters.push({
            field: selectedSearchField,
            operator: targetField?.operator || "contains",
            value: searchValue.trim(),
          });
        }
      }

      const queryFilter: FilterType | undefined = textFilters.length > 0
        ? (selectedSearchField === "all" ? { logic: "or", filters: textFilters } : textFilters[0] as any)
        : undefined;

      const combinedFilters: FilterType[] = [];
      if (queryFilter) combinedFilters.push(queryFilter);
      if (propsFilters && propsFilters.length > 0) combinedFilters.push(...propsFilters);

      let finalFilter: FilterType | undefined = undefined;
      if (combinedFilters.length > 0) {
        finalFilter = { logic: "and", filters: combinedFilters };
      }

      const newParams: QueryParams = {
        ...(queryParams || {}),
        pageIndex: 1,
        filter: finalFilter,
      };

      setQueryParams(newParams);
      fetchDataDynamic(newParams);
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [searchValue, selectedSearchField, JSON.stringify(propsFilters)]);

  useEffect(() => {
    if (initialSortMount.current) {
      initialSortMount.current = false;
      return;
    }

    if (sorting.length > 0) {
      const sortParams = sorting.map((item) => ({
        field: item.id,
        dir: item.desc ? ("desc" as const) : ("asc" as const),
      }));
      const newParams: QueryParams = { ...(queryParams || {}), sort: sortParams };
      setQueryParams(newParams);
      fetchDataDynamic(newParams);
    } else {
      const newParams: QueryParams = { ...(queryParams || {}), sort: [] };
      setQueryParams(newParams);
      fetchDataDynamic(newParams);
    }
  }, [sorting]);

  const handlePageChange = (newPageIndex: number) => {
    const newParams: QueryParams = { ...(queryParams || {}), pageIndex: newPageIndex };
    setQueryParams(newParams);
    fetchDataDynamic(newParams);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    const newParams: QueryParams = { ...(queryParams || {}), pageIndex: 1, pageSize: newPageSize };
    setQueryParams(newParams);
    fetchDataDynamic(newParams);
  };

  const handleExcelExport = async (type: "template" | "all") => {
    if (!excelModule) return;
    try {
      toast.info(type === "template" ? "Şablon indiriliyor..." : "Veri listesi Excel'e aktarılıyor...");
      await excelModule.onExport(type);
      toast.success("Excel indirme başarılı.");
    } catch {
      toast.error("Excel indirilirken hata oluştu.");
    }
  };

  const handleExcelImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!excelModule || !e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setImporting(true);
    try {
      const result = await excelModule.onImport(file);
      if (result.success) {
        toast.success(`Yükleme Başarılı! Yeni: ${result.summary.created}, Güncellenen: ${result.summary.updated}`);
        fetchDataDynamic();
      }
    } catch {
      toast.error("Excel yüklenirken teknik hata oluştu.");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const mappedColumns = useMemo<ColumnDef<T, any>[]>(() => {
    return columns.map((col) => {
      const sizeNum = typeof col.width === 'number' ? col.width : parseInt(String(col.width || 0)) || undefined;
      let minSizeNum = typeof col.minWidth === 'number' ? col.minWidth : parseInt(String(col.minWidth || 0)) || undefined;
      const maxSizeNum = typeof col.maxWidth === 'number' ? col.maxWidth : parseInt(String(col.maxWidth || 0)) || undefined;

      if (String(col.field) === "actions") {
        minSizeNum = Math.max(minSizeNum || 0, 80);
      }

      return {
        id: String(col.field),
        accessorKey: String(col.field),
        header: typeof col.header === "string" ? col.header : () => col.header,
        enableSorting: col.sortable ?? true,
        size: sizeNum,
        minSize: minSizeNum,
        maxSize: maxSizeNum,
        cell: (info) => {
          const bodyContent = col.body
            ? col.body(info.row.original, { rowIndex: info.row.index })
            : (() => {
              const val = info.getValue();
              return val !== null && val !== undefined ? String(val) : "";
            })();

          if (col.cellHoverActions) {
            return (
              <div className="group/cell relative flex items-center w-full h-full min-h-[24px] overflow-hidden">
                <div className="flex-1 min-w-0">
                  {bodyContent}
                </div>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/cell:opacity-100 transition-opacity bg-gradient-to-l from-bgAlt via-bgAlt to-transparent pl-6 pr-1 flex items-center h-full z-10 pointer-events-auto">
                  {col.cellHoverActions(info.row.original, { rowIndex: info.row.index })}
                </div>
              </div>
            );
          }

          return bodyContent;
        },
      }
    });
  }, [columns]);

  const finalColumns = useMemo(() => {
    let cols: ColumnDef<T>[] = [...mappedColumns];

    if (props.showIndex) {
      const indexColumn: ColumnDef<T> = {
        id: "index",
        header: () => <div className="text-center w-full">#</div>,
        cell: (info) => <div className="text-center font-bold text-text/50">{info.row.index + 1 + (currentPageIndex - 1) * currentPageSize}</div>,
        enableSorting: false,
        enableResizing: true,
        size: 32,
      };
      cols = [indexColumn, ...cols];
    }

    if (selectable || props.showSelection) {
      const selectColumn: ColumnDef<T> = {
        id: "select",
        header: ({ table }) => (
          <div className="flex items-center justify-center  ">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-inputBorder text-primary focus:ring-primary/20 accent-primary"
              checked={table.getIsAllPageRowsSelected()}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-center  ">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-inputBorder text-primary focus:ring-primary/20 accent-primary"
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          </div>
        ),
        enableSorting: false,
        enableResizing: true,
        size: 32,
      };
      cols = [selectColumn, ...cols];
    }

    return cols;
  }, [mappedColumns, selectable, props.showSelection, props.showIndex, currentPageIndex, currentPageSize]);


  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const saved = getTableColumns(tableKey);
    return saved?.order || [];
  });

  const [columnSizing, setColumnSizing] = useState<Record<string, number>>(() => {
    return getTableColumns(tableKey)?.widths || {};
  });

  useEffect(() => {
    const saved = getTableColumns(tableKey);
    const orderFromSaved = saved?.order || [];
    const newIds = finalColumns.map(c => c.id as string);
    const ordered = [...orderFromSaved.filter(id => newIds.includes(id)), ...newIds.filter(id => !orderFromSaved.includes(id))];
    setColumnOrder(prev => {
      if (prev.length === ordered.length && prev.every((v, i) => v === ordered[i])) return prev;
      return ordered;
    });
  }, [finalColumns, tableKey, getTableColumns]);

  const table = useReactTable({
    data: data || [],
    columns: finalColumns,
    state: { sorting, columnVisibility, rowSelection, columnOrder, columnSizing },
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  useEffect(() => {
    if (table.getState().columnSizingInfo.isResizingColumn === false) {
      const existingConfig = getTableColumns(tableKey) || { columns: [] };
      setTableColumns(tableKey, {
        ...existingConfig,
        widths: columnSizing
      });
    }
  }, [table.getState().columnSizingInfo.isResizingColumn, columnSizing, tableKey, setTableColumns, getTableColumns]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  // --- Virtual Scrolling (Sanallaştırma) Kurulumu ---
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rows = table.getRowModel().rows;

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 50, // Tahmini ortalama satır yüksekliği
    overscan: 5,
  });

  const virtualRows = virtualize ? rowVirtualizer.getVirtualItems() : null;
  const totalVirtualSize = virtualize ? rowVirtualizer.getTotalSize() : 0;

  const paddingTop = virtualRows && virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
  const paddingBottom = virtualRows && virtualRows.length > 0 ? totalVirtualSize - (virtualRows[virtualRows.length - 1]?.end || 0) : 0;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((order) => {
        const oldIndex = order.indexOf(active.id as string);
        const newIndex = order.indexOf(over.id as string);
        const newOrder = arrayMove(order, oldIndex, newIndex);

        const existingConfig = getTableColumns(tableKey) || { columns: [] };
        setTableColumns(tableKey, { ...existingConfig, order: newOrder });

        return newOrder;
      });
    }
  };

  const handleResetSettings = () => {
    // 1. Remove from localStorage
    localStorage.removeItem(`table_cols_${tableKey}`);

    // 2. Clear from uiStore
    setTableColumns(tableKey, { columns: [], widths: {}, order: [] });

    // 3. Reset local states
    const defaultMap: Record<string, boolean> = {};
    columns.forEach((col) => {
      defaultMap[String(col.field)] = true;
    });
    setColumnVisibility(defaultMap);
    setColumnOrder([]);
    setColumnSizing({});
    setSorting([]);

    toast.success("Tablo ayarları varsayılana sıfırlandı.");
  };

  const actionButtonsNode = (
    <>
      {excelModule && (
        <div className="flex items-center p-1 bg-bgAlt/50 rounded-2xl border border-borderColor  w-full lg:w-auto">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleExcelImportChange}
            accept=".xlsx, .xls"
            className="hidden"
          />
          <button
            onClick={() => { fileInputRef.current?.click(); setShowMobileActions(false); }}
            disabled={importing}
            className="flex-1 lg:flex-none flex justify-center p-2.5 rounded-xl hover:bg-white dark:hover:bg-card text-text  hover:text-primary hover:shadow-sm transition-all active:scale-95 disabled:opacity-50"
            title="Excel'den Yükle"
          >
            <Upload size={18} className={importing ? "animate-spin" : ""} />
          </button>

          <button
            onClick={() => { handleExcelExport("template"); setShowMobileActions(false); }}
            className="flex-1 lg:flex-none flex justify-center p-2.5 rounded-xl hover:bg-white dark:hover:bg-card text-text  hover:text-primary hover:shadow-sm transition-all active:scale-95"
            title="Boş Şablon İndir"
          >
            <FileSpreadsheet size={18} />
          </button>
          <button
            onClick={() => { handleExcelExport("all"); setShowMobileActions(false); }}
            className="flex-1 lg:flex-none flex justify-center p-2.5 rounded-xl hover:bg-white dark:hover:bg-card text-text hover:text-primary hover:shadow-sm transition-all active:scale-95"
            title="Tümünü Excel'e Aktar"
          >
            <Download size={18} />
          </button>
        </div>
      )}

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className="flex items-center justify-center p-2.5 rounded-2xl bg-inputBg border border-inputBorder hover:bg-inputHover text-text hover:text-primary transition-all active:scale-95 shadow-sm w-full lg:w-auto"
            title="Sütunlar"
          >    <SlidersHorizontal size={16} />

          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            onInteractOutside={(e) => {
              const target = e.target as Element;
              if (target.closest('[role="menuitemcheckbox"]') || target.closest('[data-radix-popper-content-wrapper]')) {
                e.preventDefault();
              }
            }}
            className="min-w-[200px] max-h-[350px] overflow-y-auto custom-scrollbar bg-card border border-borderColor rounded-xl shadow-[0_10px_40px_rgb(0,0,0,0.15)] p-2 z-[9999] text-text select-none focus:outline-none data-[state=open]:animate-modalIn data-[state=closed]:animate-modalOut origin-top-right"
          >
            <DropdownMenu.Label className="text-[10px] font-black uppercase tracking-widest text-text/40 px-3 py-2">
              Görünür Sütunlar
            </DropdownMenu.Label>
            {table.getAllLeafColumns().map((column) => {
              if (column.id === "actions") return null;

              let headerLabel = column.id;
              if (typeof column.columnDef.header === 'string') {
                headerLabel = column.columnDef.header;
              } else if (column.id === 'index') {
                headerLabel = 'Sıra No';
              } else if (column.id === 'select') {
                headerLabel = 'Seçim';
              }

              return (
                <DropdownMenu.CheckboxItem
                  key={column.id}
                  checked={column.getIsVisible()}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(value) => column.toggleVisibility(!!value)}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl hover:bg-primary/10 hover:text-primary cursor-pointer focus:outline-none select-none transition-colors"
                >
                  <div className={`w-4 h-4 rounded-md border flex items-center justify-center transition-colors ${column.getIsVisible() ? 'bg-primary border-primary text-white' : 'border-borderColor'}`}>
                    {column.getIsVisible() && <Check size={12} strokeWidth={3} />}
                  </div>
                  <span>{headerLabel}</span>
                </DropdownMenu.CheckboxItem>
              );
            })}
            <DropdownMenu.Separator className="h-px bg-borderColor my-1" />
            <DropdownMenu.Item
              onSelect={(e) => {
                e.preventDefault();
                handleResetSettings();
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer outline-none transition-colors select-none"
            >
              <RotateCcw size={14} />
              <span className="font-medium">Ayarları Sıfırla</span>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <button
        onClick={() => { fetchDataDynamic(); setShowMobileActions(false); }}
        className="flex items-center justify-center p-2.5 rounded-2xl bg-inputBg border border-inputBorder hover:bg-inputHover text-text hover:text-primary transition-all active:scale-95 shadow-sm w-full lg:w-auto"
        title="Yenile"
      >
        <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
      </button>

      {actions && (
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2 w-full lg:w-auto" onClick={() => setShowMobileActions(false)}>
          {actions}
        </div>
      )}
    </>
  );

  const handleRowClick = (e: React.MouseEvent, row: any) => {
    const target = e.target as HTMLElement;
    // Eğer tıklanan eleman bir buton, link, form girdisi, açılır menü veya aksiyon hücresi ise satır seçimini tetikleme
    const isInteractive = target.closest(
      'button, a, input, select, textarea, [role="button"], [role="menuitem"], [data-radix-collection-item], .table-actions, [data-interactive="true"], .p-dropdown, .p-multiselect, .p-calendar, .p-overlaypanel'
    );
    if (isInteractive) {
      return;
    }

    if (selectable || props.showSelection) {
      row.toggleSelected();
    }

    if (props.onRowClick) {
      props.onRowClick(row.original, e);
    }
  };

  const totalPages = Math.max(1, Math.ceil((total || 0) / currentPageSize));

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPageIndex - Math.floor(maxVisiblePages / 2));
    let endPage = startPage + maxVisiblePages - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button
          key="first"
          onClick={() => handlePageChange(1)}
          className="flex items-center justify-center min-w-[32px] h-8 px-2 rounded-lg text-xs font-bold transition-all active:scale-95 shadow-sm bg-inputBg border border-inputBorder text-text/70 hover:text-primary hover:border-primary/50"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <div key="dots1" className="flex items-center justify-center w-8 h-8 text-text/40">
            <MoreHorizontal size={14} />
          </div>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`flex items-center justify-center min-w-[32px] h-8 px-2 rounded-lg text-xs font-bold transition-all active:scale-95 shadow-sm ${currentPageIndex === i
            ? "bg-primary text-white shadow-md shadow-primary/20 border-transparent"
            : "bg-inputBg border border-inputBorder text-text/70 hover:text-primary hover:border-primary/50"
            }`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <div key="dots2" className="flex items-center justify-center w-8 h-8 text-text/40">
            <MoreHorizontal size={14} />
          </div>
        );
      }
      pages.push(
        <button
          key="last"
          onClick={() => handlePageChange(totalPages)}
          className="flex items-center justify-center min-w-[32px] h-8 px-2 rounded-lg text-xs font-bold transition-all active:scale-95 shadow-sm bg-inputBg border border-inputBorder text-text/70 hover:text-primary hover:border-primary/50"
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  const tableContent = (
    <div className={`flex flex-col text-text transition-all duration-300 ${isFullScreen
      ? "h-full w-full"
      : "relative h-full w-full bg-transparent"
      }`}>
      {typeof document !== 'undefined' && createPortal(
        <>
          <div
            className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-[998] transition-opacity duration-500 lg:hidden ${showMobileActions ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
            onClick={() => setShowMobileActions(false)}
          />
          <div className={`
            fixed top-0 right-0 h-full w-full sm:w-[320px] bg-card border-l border-borderColor shadow-[0_0_40px_rgba(0,0,0,0.1)] z-[999] flex flex-col transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] lg:hidden
            ${showMobileActions ? "translate-x-0" : "translate-x-full"}
          `}>
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-borderColor bg-bgAlt/30">
              <h3 className="text-sm font-black text-text tracking-tight">İşlemler</h3>
              <button onClick={() => setShowMobileActions(false)} className="p-2.5 bg-inputBg border border-inputBorder rounded-xl hover:bg-inputHover hover:text-primary text-text transition-all active:scale-95 shadow-sm">
                <X size={16} strokeWidth={3} />
              </button>
            </div>
            {/* Drawer Body */}
            <div className="flex flex-col gap-4 p-6 overflow-y-auto custom-scrollbar pb-10">
              {filterBar && (
                <div className="block sm:hidden mb-2">
                  <div className="text-xs font-bold text-text uppercase tracking-wider mb-2">Filtreler</div>
                  {filterBar}
                  <div className="h-px bg-borderColor mt-4"></div>
                </div>
              )}
              {actionButtonsNode}
            </div>
          </div>
        </>,
        document.body
      )}

      <div className={`flex-1 min-h-0 flex flex-col bg-card/95 backdrop-blur-3xl shadow-[0_20px_50px_rgba(0,0,0,0.05)] border-borderColor overflow-hidden relative transition-all duration-300 ${isFullScreen
        ? "rounded-none m-0 border-0"
        : "h-full rounded-[0.5rem] border"
        }`}>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-3 sm:px-6 sm:pt-5 border-b border-borderColor bg-card">
          <div className="flex-none flex flex-row items-center justify-between w-full lg:w-auto gap-4">
            <div>
              <h1 className="text-xl sm:text-1xl font-black text-text tracking-tight">{title}</h1>
              {subTitle && <div className="text-sm font-medium text-text/50 mt-1">{subTitle}</div>}
            </div>

            <div className="flex lg:hidden items-center gap-2">
              <button
                onClick={() => setIsFullScreen(!isFullScreen)}
                className={`flex p-2.5 rounded-2xl border transition-all active:scale-95 shadow-sm ${isFullScreen
                  ? "bg-primary border-primary text-white"
                  : "bg-inputBg border-inputBorder text-text hover:bg-inputHover hover:text-text"
                  }`}
                title={isFullScreen ? "Tam Ekrandan Çık" : "Tam Ekran Yap"}
              >
                {isFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>

              {filterBar && (
                <button
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  className={`flex p-2.5 rounded-2xl border transition-all active:scale-95 shadow-sm ${isFilterExpanded
                    ? "bg-primary border-primary text-white"
                    : "bg-inputBg border-inputBorder text-text hover:bg-inputHover hover:text-text"
                    }`}
                  title={isFilterExpanded ? "Filtreleri Gizle" : "Filtreleri Göster"}
                >
                  {isFilterExpanded ? <FilterX size={16} /> : <Filter size={16} />}
                </button>
              )}
            </div>
          </div>

          {/* Wrapper for Search and Actions */}
          <div className="flex items-center gap-3 w-full lg:w-auto lg:flex-1 lg:justify-end">
            <div className="relative group flex items-stretch flex-1 lg:max-w-md mx-auto lg:mx-0 rounded-2xl bg-inputBg border border-inputBorder focus-within:bg-inputFocus focus-within:border-primary/50 focus-within:ring-4 focus-within:ring-primary/10 hover:border-inputBorder hover:bg-inputHover transition-all duration-300 shadow-inner">
              {/* Sol Dropdown Seçici */}
              {searchFieldOptions.length > 1 && (
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 px-3.5 py-2.5 text-xs font-bold text-text/70 hover:text-primary bg-bgAlt/60 hover:bg-bgAlt border-r border-inputBorder rounded-l-2xl transition-all cursor-pointer select-none shrink-0 outline-none group-focus-within:border-primary/30"
                      title="Arama Alanını Seçin"
                    >
                      <span className="truncate max-w-[85px] sm:max-w-[110px]">
                        {currentSearchFieldLabel}
                      </span>
                      <ChevronDown size={13} className="opacity-60 shrink-0" />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      align="start"
                      className="min-w-[180px] max-h-[320px] overflow-y-auto custom-scrollbar bg-card border border-borderColor rounded-2xl shadow-[0_10px_40px_rgb(0,0,0,0.15)] p-1.5 z-[9999] text-text select-none focus:outline-none data-[state=open]:animate-modalIn data-[state=closed]:animate-modalOut origin-top-left"
                    >
                      <DropdownMenu.Label className="text-[10px] font-black uppercase tracking-widest text-text/40 px-3 py-1.5">
                        Arama Kapsamı
                      </DropdownMenu.Label>
                      <DropdownMenu.Item
                        onSelect={() => setSelectedSearchField("all")}
                        className={`flex items-center justify-between px-3 py-2 text-xs font-medium rounded-xl cursor-pointer outline-none transition-colors ${selectedSearchField === "all" ? "bg-primary/10 text-primary font-bold" : "hover:bg-bgAlt text-text"
                          }`}
                      >
                        <span>Tüm Alanlarda Ara</span>
                        {selectedSearchField === "all" && <Check size={14} className="text-primary" />}
                      </DropdownMenu.Item>
                      <DropdownMenu.Separator className="h-px bg-borderColor my-1" />
                      {searchFieldOptions.map((opt) => (
                        <DropdownMenu.Item
                          key={opt.value}
                          onSelect={() => setSelectedSearchField(opt.value)}
                          className={`flex items-center justify-between px-3 py-2 text-xs font-medium rounded-xl cursor-pointer outline-none transition-colors ${selectedSearchField === opt.value ? "bg-primary/10 text-primary font-bold" : "hover:bg-bgAlt text-text"
                            }`}
                        >
                          <span className="truncate mr-2">{opt.label}</span>
                          {selectedSearchField === opt.value && <Check size={14} className="text-primary shrink-0" />}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              )}

              {/* Arama Metin Girdisi */}
              <div className="relative flex-1 flex items-center min-w-0">
                <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 text-text/40 group-focus-within:text-primary transition-colors pointer-events-none ${searchFieldOptions.length > 1 ? 'hidden sm:block' : 'block'}`} />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={
                    selectedSearchField !== "all"
                      ? `${currentSearchFieldLabel} içinde ara...`
                      : (placeholder || "Ara...")
                  }
                  className={`w-full ${searchFieldOptions.length > 1 ? 'pl-3 sm:pl-9' : 'pl-9'} pr-9 py-2.5 text-sm font-semibold bg-transparent focus:outline-none text-inputText placeholder:text-text/30`}
                />
                {searchValue && (
                  <button
                    type="button"
                    onClick={() => setSearchValue("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text/40 hover:text-text bg-card border border-borderColor rounded-full p-1 transition-colors shadow-xs"
                  >
                    <X size={12} strokeWidth={3} />
                  </button>
                )}
              </div>
            </div>

            <div className="shrink-0 flex items-center justify-end gap-2">
              <button
                onClick={() => setIsFullScreen(!isFullScreen)}
                className={`hidden lg:flex p-2.5 rounded-2xl border transition-all active:scale-95 shadow-sm ${isFullScreen
                  ? "bg-primary border-primary text-white"
                  : "bg-inputBg border-inputBorder text-text hover:bg-inputHover hover:text-text"
                  }`}
                title={isFullScreen ? "Tam Ekrandan Çık" : "Tam Ekran Yap"}
              >
                {isFullScreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>

              {filterBar && (
                <button
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  className={`hidden lg:flex p-2.5 rounded-2xl border transition-all active:scale-95 shadow-sm ${isFilterExpanded
                    ? "bg-primary border-primary text-white"
                    : "bg-inputBg border-inputBorder text-text hover:bg-inputHover hover:text-text"
                    }`}
                  title={isFilterExpanded ? "Filtreleri Gizle" : "Filtreleri Göster"}
                >
                  {isFilterExpanded ? <FilterX size={18} /> : <Filter size={18} />}
                </button>
              )}

              <div className="hidden lg:flex flex-wrap items-center justify-end gap-3 ml-1">
                {actionButtonsNode}
              </div>
              <button
                onClick={() => setShowMobileActions(true)}
                className="lg:hidden p-3 rounded-2xl bg-inputBg border border-inputBorder text-text/70 hover:bg-inputHover hover:text-primary transition-all active:scale-95 shadow-sm ml-1"
              >
                <MoreVertical size={20} />
              </button>
            </div>
          </div>
        </div>

        {filterBar && (
          <div
            className={`hidden sm:block transition-all duration-300 ease-in-out overflow-hidden ${isFilterExpanded ? "max-h-[500px] opacity-100 border-b border-borderColor py-3" : "max-h-0 opacity-0 border-b-0 py-0"
              }`}
          >
            <div className="px-2 sm:px-4 bg-bgAlt/10 h-full">
              {filterBar}
            </div>
          </div>
        )}

        <div ref={tableContainerRef} className="flex-1 overflow-auto relative min-h-0 custom-scrollbar rounded-b-none">
          {loading && (
            <div className="sticky top-0 left-0 right-0 h-[2.5px] w-full bg-gradient-to-r from-primary/30 via-primary to-primary/30 animate-pulse z-[30] pointer-events-none" />
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToHorizontalAxis]} onDragEnd={handleDragEnd}>
            <table style={{ tableLayout: "fixed", width: table.getTotalSize() }} className="text-left border-collapse select-none ">
              <thead className="sticky top-0 bg-card z-[10] select-none shadow-[0_1px_0_0_var(--border)]">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                      {headerGroup.headers.map((header) => {
                        const alignClasses = { left: "text-left", center: "text-center", right: "text-right" };
                        return (
                          <DraggableTableHeader
                            key={header.id}
                            header={header}
                            alignClasses={alignClasses}
                            columns={columns}
                            tableKey={tableKey}
                          />
                        );
                      })}
                    </SortableContext>
                  </tr>
                ))}
              </thead>
              <tbody className="select-none relative">


                {loading && (!data || data.length === 0) ? (
                  <tr>
                    <td colSpan={columns.length + (selectable ? 1 : 0)} className="py-24 px-6 text-center">
                      <div className="flex flex-col items-center justify-center gap-3 text-text/50">
                        <Loader2 size={36} className="animate-spin text-primary" />
                        <span className="text-xs font-bold uppercase tracking-wider">Veriler Yükleniyor...</span>
                      </div>
                    </td>
                  </tr>
                ) : rows.length > 0 ? (
                  <>
                    {virtualize && paddingTop > 0 && (
                      <tr>
                        <td style={{ height: `${paddingTop}px` }} colSpan={columns.length + (selectable ? 1 : 0)} />
                      </tr>
                    )}

                    {(virtualize && virtualRows ? virtualRows : rows.map((r, i) => ({ index: i }))).map((virtualRow) => {
                      const row = rows[virtualRow.index];
                      const isRowSelected = row.getIsSelected();
                      return (
                        <tr
                          key={row.id}
                          data-index={virtualRow.index}
                          ref={virtualize ? rowVirtualizer.measureElement : null}
                          onClick={(e) => handleRowClick(e, row)}
                          className={`transition-colors duration-200 group select-none ${selectable || props.showSelection || props.onRowClick ? 'cursor-pointer' : ''
                            } ${isRowSelected
                              ? 'bg-primary/10 dark:bg-primary/20 hover:bg-primary/15 dark:hover:bg-primary/25'
                              : 'hover:bg-bgAlt'
                            }`}
                        >
                          {row.getVisibleCells().map((cell) => {
                            const colDef = columns.find(c => String(c.field) === cell.column.id);
                            const alignMap = colDef?.align || "left";
                            const textOverflowMap = colDef?.textOverflow;
                            const alignClasses = { left: "text-left", center: "text-center", right: "text-right" };
                            const isStatic = cell.column.id === 'select' || cell.column.id === 'index' || cell.column.id === 'actions';

                            let overflowClasses = "";
                            let overflowStyle: React.CSSProperties = {};

                            let tdStyle: React.CSSProperties = {
                              width: cell.column.getSize(),
                              minWidth: cell.column.getSize(),
                              maxWidth: cell.column.getSize()
                            };

                            if (colDef?.maxLines) {
                              overflowClasses = "whitespace-normal break-words";
                              overflowStyle = {
                                display: '-webkit-box',
                                WebkitLineClamp: colDef.maxLines,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              };
                            } else if (textOverflowMap === "ellipsis") {
                              overflowClasses = "whitespace-nowrap";
                              overflowStyle = { overflow: "hidden", textOverflow: "ellipsis" };
                            } else if (textOverflowMap === "scroll") {
                              overflowClasses = "whitespace-nowrap custom-scrollbar pb-0.5";
                              overflowStyle = { overflowX: "auto" };
                            } else if (textOverflowMap === "wrap") {
                              overflowClasses = "whitespace-normal break-words";
                            } else {
                              // Default behavior
                              overflowClasses = "whitespace-nowrap";
                              overflowStyle = { overflow: "hidden", textOverflow: "ellipsis" };
                            }

                            // Eğer kolon çok daraltıldıysa (örn: 100px altı) paddingleri sıfırlayarak alandan tasarruf et
                            const isSquished = cell.column.getSize() < 100;
                            const paddingClasses = isStatic
                              ? 'px-0'
                              : isSquished
                                ? 'py-0  '
                                : 'py-1 sm:pl-2 pr-2 sm:pr-3';

                            return (
                              <td key={cell.id} style={tdStyle} className={`text-sm text-text/80 font-medium border border-borderColor ${alignClasses[alignMap]} ${paddingClasses}`}>
                                <div className={overflowClasses} style={overflowStyle}>
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}

                    {virtualize && paddingBottom > 0 && (
                      <tr>
                        <td style={{ height: `${paddingBottom}px` }} colSpan={columns.length + (selectable ? 1 : 0)} />
                      </tr>
                    )}
                  </>
                ) : (
                  <tr>
                    <td colSpan={columns.length + (selectable ? 1 : 0)} className="py-20 px-6   ">
                      <div className="sticky left-0 right-0 mx-auto w-max flex flex-col items-center justify-center text-text/30">
                        <Search size={48} strokeWidth={1} className="mb-4 text-text/20" />
                        <p className="text-base font-bold">Sonuç Bulunamadı</p>
                        <p className="text-sm mt-1">Girdiğiniz kriterlere uyan bir kayıt yok.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </DndContext>
        </div>

        <div className="flex flex-row items-center justify-between gap-2 sm:gap-4 px-4 sm:px-8 py-4 sm:py-5 border-t border-borderColor bg-card select-none w-full">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-text/50 uppercase tracking-widest hidden sm:inline">Görünüm:</span>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button className="flex items-center justify-between gap-2 min-w-[85px] pl-3 pr-2 py-1.5 text-xs font-bold rounded-xl bg-bgAlt/50 border border-borderColor hover:bg-bgAlt hover:border-primary focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all text-text/80 active:scale-95 shadow-sm">
                  <span>{currentPageSize} Satır</span>
                  <ChevronDown size={14} className="text-text" />
                </button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="start"
                  sideOffset={6}
                  className="bg-dropdownBg border border-borderColor rounded-xl shadow-[0_8px_30px_var(--shadow)] p-1 z-[9999] min-w-[90px]"
                >
                  {ROWS_PER_PAGE_OPTIONS.map((opt) => (
                    <DropdownMenu.Item
                      key={opt}
                      onClick={() => handlePageSizeChange(opt)}
                      className={`flex items-center px-3 py-2 text-xs font-bold rounded-lg cursor-pointer focus:outline-none transition-colors select-none ${currentPageSize === opt
                        ? "bg-primary text-text shadow-md shadow-primary/20"
                        : "text-text hover:bg-dropdownHover hover:text-primary"
                        }`}
                    >
                      {opt} Satır
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
            <span className="text-xs font-medium text-text/40 hidden lg:inline ml-2">
              <strong className="text-text/70">{total || 0}</strong> kayıttan <strong className="text-text/70">{Math.min((currentPageIndex - 1) * currentPageSize + 1, total || 0) || 0} - {Math.min(currentPageIndex * currentPageSize, total || 0) || 0}</strong> arası gösteriliyor.
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => handlePageChange(currentPageIndex - 1)}
              disabled={currentPageIndex === 1 || loading}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-inputBg border border-inputBorder text-text/70 hover:text-primary hover:border-primary/50 disabled:opacity-30 disabled:pointer-events-none transition-all active:scale-95 shadow-sm"
              title="Önceki Sayfa"
            >
              <ChevronLeft size={14} strokeWidth={2.5} />
            </button>

            <div className="flex items-center gap-1 mx-1 hidden sm:flex">
              {renderPageNumbers()}
            </div>

            <div className="flex sm:hidden items-center justify-center px-3 h-8 rounded-lg bg-inputBg border border-inputBorder text-xs font-bold text-text/70 mx-1">
              {currentPageIndex} / {totalPages}
            </div>

            <button
              onClick={() => handlePageChange(currentPageIndex + 1)}
              disabled={currentPageIndex >= totalPages || loading}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-inputBg border border-inputBorder text-text/70 hover:text-primary hover:border-primary/50 disabled:opacity-30 disabled:pointer-events-none transition-all active:scale-95 shadow-sm"
              title="Sonraki Sayfa"
            >
              <ChevronRight size={14} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!isFullScreen && (
        <div className="relative w-full h-full">
          {tableContent}
        </div>
      )}

      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {isFullScreen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-0 z-[999] bg-bg flex flex-col p-0 m-0 w-screen h-screen"
            >
              {tableContent}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};
